/**
 * Register watch — new HRB entries on handelsregister.de, scored for DiGA leads.
 *
 * Wire shapes mirror `system/services/register_watch/review.py ::
 * serialize_entry` / `watch_status` and `control.py :: list_sequences` —
 * change them together.
 */
import { api } from "./client";

export type RegisterReviewStatus =
  | "filtered"
  | "low_score"
  | "pending_review"
  | "proposed"
  | "skipped"
  | "not_new";
export type PausedKind =
  | "initial"
  | "manual"
  | "http_status"
  | "blocked"
  | "captcha"
  | "unexpected"
  | "errors"
  | "session";

export interface RegisterRequest {
  at: string | null;
  kind: string;
  court_code: string | null;
  register_number: number | null;
  outcome: string;
  http_status: number | null;
  duration_ms: number | null;
  note: string | null;
}

export interface RegisterWatchStatus {
  paused: boolean;
  paused_kind: PausedKind | null;
  paused_reason: string | null;
  paused_at: string | null;
  alert_sent_at: string | null;
  env_disabled: boolean;
  quiet_hours: string | null;
  hourly_cap: number;
  requests_last_hour: number;
  requests_today: number;
  requests_total: number;
  last_request_at: string | null;
  last_hit_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  backoff_until: string | null;
  announcements_refreshed_at: string | null;
  sequences: Record<string, number>;
  entries: Partial<Record<RegisterReviewStatus, number>>;
  /** Live entries (walks, holes) waiting for their purpose fetch. */
  purpose_pending: number;
  /** Look back imports waiting for their purpose fetch (background share). */
  lookback_pending: number;
  /** Ranges whose next walk is due, and when the longest-waiting one fell due. */
  walks_due: number;
  walk_due_since: string | null;
  new_today: number;
  recent_requests?: RegisterRequest[];
}

export interface RegisterEntry {
  id: string;
  court_code: string;
  court_name: string;
  register_number: number;
  register_suffix: string | null;
  name: string;
  seat: string | null;
  portal_status: string;
  former_court: string | null;
  origin: string;
  first_seen_at: string | null;
  name_verdict: string;
  name_reason: string | null;
  purpose_status: string;
  purpose: string | null;
  legal_form_code: string | null;
  capital_amount: string | null;
  capital_currency: string | null;
  founded_on: string | null;
  entries_count: number | null;
  postal_code: string | null;
  directors_count: number | null;
  score: number | null;
  matched_terms: string[];
  review_status: RegisterReviewStatus;
  review_note: string | null;
  reviewed_at: string | null;
  candidate_id: string | null;
}

export interface RegisterEntryList {
  entries: RegisterEntry[];
  count: number;
  total: number;
}

export const registerWatchApi = {
  status: () => api.get<RegisterWatchStatus>("/api/v1/register-watch/status"),
  entries: (params: { status?: string; min_score?: number; limit?: number } = {}) =>
    api.get<RegisterEntryList>("/api/v1/register-watch/entries", params),
  entry: (id: string) => api.get<RegisterEntry>(`/api/v1/register-watch/entries/${id}`),
  resume: (note?: string) =>
    api.post<RegisterWatchStatus>("/api/v1/register-watch/resume", { note: note?.trim() || undefined }),
  pause: (reason: string) => api.post<RegisterWatchStatus>("/api/v1/register-watch/pause", { reason }),
};
